import Modal from 'components/common/Modal';
import axios from 'helpers/axios';
import config from 'helpers/config';
import Service from 'models/ServiceModel';
import React from 'react';
import SignatureCanvas from 'react-signature-canvas';

interface Props {
    repairOrderId: string;
    approvedServices: Service[];
    declinedServices: Service[];
    onClose: () => void;
    onAuthorized: () => void;
}

interface State {
    isSubmitting: boolean;
    hasSignature: boolean;
    error?: string;
}

class SignatureAuthorizationModal extends React.Component<Props, State> {
    // Instance ref, not a module-level one: the customer can open, cancel and
    // reopen this modal on the same page, and a shared ref would still point at
    // the unmounted canvas.
    private signatureRef: any = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            isSubmitting: false,
            hasSignature: false,
            error: undefined,
        };

        this.authorize = this.authorize.bind(this);
        this.clear = this.clear.bind(this);
    }

    clear() {
        if (this.state.isSubmitting) return;
        this.signatureRef?.clear();
        this.setState({ hasSignature: false, error: undefined });
    }

    authorize() {
        // Guard the submit itself, not just the button: this is the one place
        // that can create an authorization, and a second in-flight POST would
        // record a duplicate.
        if (this.state.isSubmitting) return;

        if (!this.signatureRef || this.signatureRef.isEmpty()) {
            this.setState({ error: 'Please sign in the box above before authorizing.' });
            return;
        }

        const authorization = {
            authorizationSignature: JSON.stringify(this.signatureRef.toData()),
            repairOrderId: this.props.repairOrderId,
            authorizedServiceIds: this.props.approvedServices.map((service) => service.id),
            declinedServiceIds: this.props.declinedServices.map((service) => service.id),
        };

        this.setState({ isSubmitting: true, error: undefined });

        axios
            .post(`${config.apiUrl}/authorization/customer`, authorization)
            .then(() => {
                this.props.onAuthorized();
            })
            .catch((error) => {
                this.setState({
                    isSubmitting: false,
                    error: error?.message || 'Something went wrong. Please try again or call the shop.',
                });
            });
    }

    render() {
        const { approvedServices, declinedServices } = this.props;
        const { isSubmitting, hasSignature, error } = this.state;

        return (
            <Modal
                headerText={'Signature Authorization'}
                isOpen={true}
                onClose={() => !isSubmitting && this.props.onClose()}
                isBackgroundWhite={true}
                isDeleteHidden={true}
                saveText={isSubmitting ? 'Submitting...' : 'Authorize'}
                onSave={this.authorize}
                isSaveHidden={false}
                isSaveDisabled={isSubmitting || !hasSignature}
                isLoading={isSubmitting}
                onCancel={this.clear}
                cancelText="Clear"
            >
                <div className="w-full">
                    <div className="mb-3">
                        <p className="font-semibold">
                            By signing you authorize {approvedServices.length} service
                            {approvedServices.length === 1 ? '' : 's'}
                            {declinedServices.length > 0 &&
                                ` and decline ${declinedServices.length} service${
                                    declinedServices.length === 1 ? '' : 's'
                                }`}
                            .
                        </p>
                        <p className="text-sm text-gray-600">
                            Your decision is recorded once and cannot be changed here afterwards - contact the shop if
                            you need to adjust it.
                        </p>
                    </div>

                    <SignatureCanvas
                        ref={(ref: any) => {
                            this.signatureRef = ref;
                        }}
                        onEnd={() => this.setState({ hasSignature: true, error: undefined })}
                        backgroundColor="#f0f1fb"
                        penColor="black"
                        canvasProps={{ height: 400, className: 'w-full' }}
                    />

                    {!!error && <div className="mt-3 font-semibold text-red-600">{error}</div>}
                </div>
            </Modal>
        );
    }
}

export default SignatureAuthorizationModal;
